import { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ContactsTypes, translateReverse } from 'api/types/ReferenceData';
import { Countries } from 'api/types/countries';
import {
  ContactRequest,
  LastRequest,
  RequestTypes
} from 'types/ContactRequests';
import { formatProducts, ValidPrefillData } from './helpers/helpers';
import { UserContext } from '../../../context/User';
import { useTranslation } from 'react-i18next';
import { submitRequestByContext } from './helpers/requestsSubmit';
import piwik, { trackSearchEvent } from 'piwik/piwik';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid2 as Grid,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import BaseRequestModal from './BaseRequestModal';
import { UnitField } from '../Select/UnitField';
import { currentPlatform, DATE_FORMAT } from 'components/constants/Constants';
import { QuantityUnitType } from 'api/types/ReferenceDataTS';
import { RequestContactProps } from './types';
import { PhoneField } from 'components/pages/register/Register/fields/PhoneField';
import dayjs from 'dayjs';
import {
  CustomContactSection,
  CustomContactSectionFormState
} from './CustomContactSection/CustomContactSection';
import { useBrandPortalEventKey } from 'hooks/useBrandPortalEventKey';

type RequestQuoteOrSampleDialogFormState = {
  phoneNumber: string;
  request: {
    quantityRequired: { value: string; unit: string };
    estimateUsage: { value: string; unit: string };
    message: string;
    neededBy?: string;
  };
  shippingAddress: {
    company: string;
    personOfContact: string;
    city: string;
    country: string;
    countryCode?: string;
    postcode: string;
    street: string;
    street2: string;
  };
  acknowledge: boolean;
} & CustomContactSectionFormState;

const RequestQuoteOrSampleDialog = ({
  setModal,
  context,
  requestType,
  fullProducts,
  project,
  searchContext
}: RequestContactProps) => {
  const user = useContext(UserContext);

  const i18n = useTranslation();

  const methods = useForm<RequestQuoteOrSampleDialogFormState>();
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors, isSubmitted }
  } = methods;
  const [lastRequest, setLastRequest] = useState<LastRequest>();
  const [closeVisible, setCloseVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const loadLastRequest = () => {
    if (!lastRequest) return;
    const {
      quantityRequired,
      quantityRequiredUnits,
      estimateUsage,
      estimatedUsageUnits,
      message,
      shippingAddress,
      phoneNumber
    } = lastRequest;
    setValue('request', {
      quantityRequired: {
        value: quantityRequired,
        unit: quantityRequiredUnits
      },
      estimateUsage: { value: estimateUsage, unit: estimatedUsageUnits },
      message
    });
    setValue('phoneNumber', phoneNumber);
    setValue('shippingAddress', {
      company: shippingAddress?.company ?? user?.shipping_address?.company,
      personOfContact:
        shippingAddress?.personOfContact ??
        user?.shipping_address?.person_of_contact,
      city: shippingAddress?.city ?? user?.shipping_address?.city,
      country: shippingAddress?.country ?? user?.shipping_address?.country,
      postcode:
        shippingAddress?.postcode ?? user?.shipping_address?.postal_code,
      street: shippingAddress?.street ?? user?.shipping_address?.street,
      street2:
        shippingAddress?.street2 ?? user?.shipping_address?.street_additional
    });
  };

  //  TODO: COMPLETE THE SUBMITDATA AND VALIDATE IT PROPERLY
  const submitData = async () => {
    //manual check for init field since we dont use register for it and the form doesnt know the errorstate :( )
    if (!getValues()?.request?.quantityRequired?.value) return;

    setSubmitting(true);

    if (searchContext) {
      if (fullProducts.length === 1) {
        trackSearchEvent(
          `request-${requestType.toLowerCase()}-by-form`,
          fullProducts[0].name
        );
      } else {
        piwik.push(['trackEvent', 'Compare', 'contact-all']);
        fullProducts.forEach((doc: any) =>
          piwik.push([
            'trackEvent',
            'Compare',
            `request-${requestType.toLowerCase()}s`,
            doc.name
          ])
        );
        piwik.push(['trackGoal', '1']);
      }
    }
    const values = getValues();
    const body: Partial<ContactRequest> = {
      type: requestType.toUpperCase() as RequestTypes,
      request: {
        ...values.request,
        phoneNumber: values.phoneNumber,
        quantityRequired: values.request?.quantityRequired?.value,
        quantityRequiredUnits: values.request?.quantityRequired?.unit,
        estimatedUsageUnits: values.request?.estimateUsage?.unit ?? '',
        estimateUsage: values.request?.estimateUsage?.value ?? '',
        shippingAddress: {
          ...values.shippingAddress,
          countryCode: translateReverse(
            values.shippingAddress?.country,
            Countries
          ),
          company: values.shippingAddress?.company,
          personOfContact: values.shippingAddress?.personOfContact
        }
      },
      products: formatProducts(context, fullProducts)
    };

    submitRequestByContext(
      context,
      body,
      requestType,
      setModal,
      setSubmitting,
      project
    );
  };

  const requestTypeReference = ContactsTypes.find(
    contact => contact.value === requestType
  );
  const title = `Request ${requestTypeReference?.label}`;

  const getFieldError = (field: Parameters<typeof methods.register>[0]) => {
    return isSubmitted && !watch(field);
  };

  const loadLastRequestButton = () => {
    return (
      <Button
        sx={{ mt: 1 }}
        variant="outlined"
        size="small"
        color="success"
        onClick={() => setCloseVisible(true)}
      >
        Prefill
      </Button>
    );
  };

  const brandPortalKey = useBrandPortalEventKey();

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitData)} data-testid={requestType}>
        <BaseRequestModal
          className="quote-details"
          show={true}
          title={title}
          onSubmitRequest={submitData}
          requestType={requestType}
          setModal={() => setModal(requestType)}
          setLastRequest={setLastRequest}
          loadLastRequest={loadLastRequest}
          closeVisible={closeVisible}
          setCloseVisible={setCloseVisible}
          submitting={submitting}
        >
          <div className="mb-3 request-title-div">
            <div className="request-title-sub-div">
              <Typography>
                {i18n.t(
                  'contactCenter.detail.manage_request.new_request_top_message'
                )}
              </Typography>
              <ValidPrefillData data={lastRequest}>
                <Typography>
                  {i18n.t(
                    'contactCenter.detail.manage_request.new_request_top_message2'
                  )}
                  {requestType.toLowerCase()} request.
                </Typography>
              </ValidPrefillData>
              {fullProducts?.length <= 1 && (
                <Typography>
                  {i18n.t(
                    'contactCenter.detail.manage_request.new_request_top_message3'
                  )}
                  <span className="fw-bolder"> {fullProducts[0]?.name} </span>
                </Typography>
              )}
            </div>
            <ValidPrefillData data={lastRequest}>
              {loadLastRequestButton()}
            </ValidPrefillData>
          </div>

          {brandPortalKey !== undefined && fullProducts.length === 1 && (
            <FormProvider {...methods}>
              <CustomContactSection brandPortalKey={brandPortalKey} />
            </FormProvider>
          )}

          <Typography variant="h6" gutterBottom>
            Request Details
          </Typography>

          <Stack
            sx={{
              gap: 1
            }}
          >
            <TextField
              label="Intention of request"
              {...register('request.message')}
              multiline
              value={watch('request.message') ?? ''}
              fullWidth
              rows={3}
              data-testid="intentionOfRequest"
            />
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'center'
              }}
            >
              {/* //margin here to align with other fields */}
              <Grid size={4} sx={{ mt: 1 }} data-testid="neededBy">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format={DATE_FORMAT}
                    minDate={dayjs()}
                    label="Needed by"
                    //backend needs iso string otherwise SendGrid will not be able to process the data and to send emails
                    onChange={value =>
                      setValue(
                        'request.neededBy',
                        value?.startOf('day').toISOString() ?? ''
                      )
                    }
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={4} data-testid="quantityRequired">
                <UnitField
                  onChange={values =>
                    setValue('request.quantityRequired', values)
                  }
                  value={
                    watch('request.quantityRequired') ?? { value: '', unit: '' }
                  }
                  label={
                    requestType === RequestTypes.QUOTE
                      ? 'Quantity required'
                      : 'Number of Samples'
                  }
                  required
                  isSubmitted={isSubmitted}
                  defaultUnit={
                    requestType === RequestTypes.QUOTE
                      ? QuantityUnitType.KILOGRAM
                      : QuantityUnitType.UNIT
                  }
                  disableUnitSelection
                />
              </Grid>
              <Grid size={4} data-testid="estimatedUsage">
                <UnitField
                  onChange={values => setValue('request.estimateUsage', values)}
                  value={
                    watch('request.estimateUsage') ?? { value: '', unit: '' }
                  }
                  label="Estimated Yearly Usage"
                  defaultUnit={QuantityUnitType.KILOGRAM}
                  disableUnitSelection
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack
            sx={{
              gap: 2,
              mt: 2
            }}
          >
            <Typography variant="h6">Shipping details</Typography>
            {requestType === 'SAMPLE' && (
              <>
                <Grid container spacing={2}>
                  <Grid size={8} data-testid="shippingAddressCompany">
                    <TextField
                      label="Company"
                      fullWidth
                      type="text"
                      value={watch('shippingAddress.company') ?? ''}
                      {...register('shippingAddress.company', {
                        required: true
                      })}
                      required
                      error={!!getFieldError('shippingAddress.company')}
                      helperText={
                        getFieldError('shippingAddress.company')
                          ? 'This field is required'
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid size={4} data-testid="shippingAddressStreet">
                    <TextField
                      fullWidth
                      label="Street"
                      type="text"
                      value={watch('shippingAddress.street') ?? ''}
                      {...register('shippingAddress.street', {
                        required: true
                      })}
                      required
                      error={!!getFieldError('shippingAddress.street')}
                      helperText={
                        getFieldError('shippingAddress.street')
                          ? 'This field is required'
                          : undefined
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={4} data-testid="shippingAddressStreet2">
                    <TextField
                      fullWidth
                      label="Street 2 (optional) "
                      type="text"
                      value={watch('shippingAddress.street2') ?? ''}
                      {...register('shippingAddress.street2')}
                    />
                  </Grid>
                  <Grid size={4} data-testid="shippingAddressPersonOfContact">
                    <TextField
                      fullWidth
                      label="Contact Person"
                      type="text"
                      value={watch('shippingAddress.personOfContact') ?? ''}
                      {...register('shippingAddress.personOfContact', {
                        required: true
                      })}
                      required
                      error={!!getFieldError('shippingAddress.personOfContact')}
                      helperText={
                        getFieldError('shippingAddress.personOfContact')
                          ? 'This field is required'
                          : undefined
                      }
                    />
                  </Grid>
                  <Grid size={4} data-testid="shippingAddressPostcode">
                    <TextField
                      fullWidth
                      label="Postcode"
                      type="text"
                      required
                      value={watch('shippingAddress.postcode') ?? ''}
                      {...register('shippingAddress.postcode', {
                        required: true
                      })}
                      error={!!getFieldError('shippingAddress.postcode')}
                      helperText={
                        getFieldError('shippingAddress.postcode')
                          ? 'This field is required'
                          : undefined
                      }
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <Grid container spacing={2}>
              {/* /* mt-1 is because the phgone field has a 8px margin out of the box */}
              <Grid size={4} sx={{ mt: -1 }} data-testid="phoneNumber">
                <PhoneField />
              </Grid>

              <Grid size={4} data-testid="shippingAddressCountry">
                <TextField
                  select
                  fullWidth
                  label="Country"
                  error={!!getFieldError('shippingAddress.country')}
                  helperText={
                    getFieldError('shippingAddress.country')
                      ? 'This field is required'
                      : undefined
                  }
                  value={watch('shippingAddress.country') ?? ''}
                  {...register('shippingAddress.country', {
                    required: true
                  })}
                  required
                >
                  {Countries.map(item => (
                    <MenuItem value={item.label} key={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={4} data-testid="shippingAddressCity">
                <TextField
                  fullWidth
                  label="City"
                  type="text"
                  required
                  value={watch('shippingAddress.city') ?? ''}
                  {...register('shippingAddress.city', {
                    required: true
                  })}
                  error={!!getFieldError('shippingAddress.city')}
                  helperText={
                    getFieldError('shippingAddress.city')
                      ? 'This field is required'
                      : undefined
                  }
                />
              </Grid>
            </Grid>
            <FormControl error={!!errors.acknowledge}>
              <FormControlLabel
                control={
                  <Checkbox
                    required
                    {...register('acknowledge', { required: true })}
                  />
                }
                label={`I allow ${currentPlatform} to store my information for the purpose given in this contact form.`}
                data-testid="acknowledge"
              />
              {errors?.acknowledge && (
                <FormHelperText>This field is required.</FormHelperText>
              )}
            </FormControl>
          </Stack>
        </BaseRequestModal>
      </form>
    </FormProvider>
  );
};

export default RequestQuoteOrSampleDialog;
